<template>
  <SideModal
    :show="teacher && show"
    @update:show="
      {
        if (!state.loadingSave) $emit('update:show', $event);
      }
    "
    location="left"
    close-on-overlay-click
  >
    <form @submit.prevent="save" class="modal-container">
      <div class="modal-top">
        <span class="d-flex gap-3 align-center">
          <Typography color="primary" weight="600" size="1.25rem">Edit Teacher</Typography>
          <v-icon size="x-small" @click="copyToClipboard(teacher.uid)">mdi-content-copy</v-icon>
        </span>
        <v-btn
          @click="
            {
              if (!state.loadingSave) $emit('update:show', false);
            }
          "
          variant="plain"
          color="normal"
          icon
          ><v-icon color="gray600">mdi-close</v-icon></v-btn
        >
      </div>

      <div class="modal-body">
        <template v-if="!state.passwordForm">
          <v-text-field
            :disabled="teacher.deleted"
            class="custom-input"
            color="primary"
            label="Title"
            v-model="state.teacher_title"
          ></v-text-field>
          <v-text-field
            :disabled="teacher.deleted"
            class="custom-input"
            color="primary"
            label="First Name"
            v-model="state.teacher_fname"
          ></v-text-field>
          <v-text-field
            :disabled="teacher.deleted"
            class="custom-input"
            color="primary"
            label="Last Name"
            v-model="state.teacher_lname"
          ></v-text-field>
          <v-text-field
            :disabled="teacher.deleted"
            class="custom-input"
            color="primary"
            label="Class Description"
            v-model="state.teacher_classes"
          ></v-text-field>
          <v-text-field
            :disabled="teacher.deleted"
            class="custom-input"
            color="primary"
            label="Room"
            v-model="state.room_assignment"
          ></v-text-field>
          <v-text-field
            :disabled="teacher.deleted"
            class="custom-input"
            color="primary"
            label="Virtual Room"
            v-model="state.virtual_room"
          ></v-text-field>
          <div>
            <v-text-field
              :disabled="teacher.deleted"
              class="custom-input"
              color="primary"
              label="E-Mail"
              v-model="state.email"
            ></v-text-field>
            <FormValidate :validate="state.email" email></FormValidate>
          </div>
          <div>
            <v-text-field
              :disabled="teacher.deleted"
              class="custom-input"
              color="primary"
              label="Phone Number"
              v-model="state.phone"
            ></v-text-field>
            <FormValidate
              :validate="state.phone"
              :regex="/^.{0,15}$/"
              title="Phone Number must be a string of 0 to 15 characters"
            ></FormValidate>
          </div>
          <v-text-field
            class="custom-input"
            color="primary"
            label="External ID"
            disabled
            :model-value="state.external_id"
          ></v-text-field>
        </template>
        <template v-else>
          <password-input
            class="custom-input"
            color="primary"
            label="Password"
            v-model="state.newPassword"
            autocomplete="new-password"
          ></password-input>
        </template>
        <div>
          <div style="display: flex; width: 100%; gap: 15px; justify-content: stretch">
            <v-btn
              v-if="!teacher.deleted"
              @click="setDelete(true)"
              :loading="state.loadingDelete"
              style="text-transform: capitalize; flex-grow: 1"
              color="#E5E4DB"
            >
              Delete
            </v-btn>
            <v-btn
              v-if="!teacher.deleted"
              type="submit"
              :loading="state.loadingSave"
              style="text-transform: capitalize; flex-grow: 1"
              >Save</v-btn
            >
            <v-btn
              v-else
              @click="setDelete(false)"
              :loading="state.loadingDelete"
              style="text-transform: capitalize; flex-grow: 1"
              >Restore</v-btn
            >
          </div>
          <v-btn
            variant="text"
            color="gray700"
            width="max-content"
            style="text-transform: capitalize"
            density="compact"
            class="mt-2"
            @click="state.passwordForm = !state.passwordForm"
            >{{ state.passwordForm ? "Cancel" : "Reset Teacher Password" }}</v-btn
          >
        </div>
      </div>
    </form>
  </SideModal>
</template>

<script setup lang="ts">
import type { TeachersType } from "@/api_types/school";
import SideModal from "@/components/sideModal.vue";
import Typography from "@/components/typography.vue";
import passwordInput from "@/components/passwordInput.vue";
import { watch } from "vue";
import { reactive } from "vue";
import { useAlertsStore } from "@/stores/alerts";
import useTeachers from "@/stores/school/teachers";
import FormValidate from "@/components/formValidate.vue";
import { copyToClipboard } from "@/utils";

const props = defineProps<{
  teacher: TeachersType[number];
  show: boolean;
}>();

const teachersS = useTeachers();

const state = reactive({
  loadingSave: false,
  loadingDelete: false,
  teacher_title: "",
  teacher_fname: "",
  teacher_lname: "",
  teacher_classes: "",
  room_assignment: "",
  virtual_room: "",
  email: "",
  external_id: "",
  passwordForm: false,
  newPassword: "",
  phone: ""
});

watch([() => props.teacher, () => props.show], () => {
  if (props.teacher)
    Object.assign(state, {
      teacher_title: props.teacher.teacher_title,
      teacher_fname: props.teacher.teacher_fname,
      teacher_lname: props.teacher.teacher_lname,
      teacher_classes: props.teacher.teacher_classes,
      room_assignment: props.teacher.room_assignment,
      virtual_room: props.teacher.virtual_room,
      email: props.teacher.email,
      external_id: props.teacher.external_id,
      phone: props.teacher.phone
    });
});

defineEmits<{
  (e: 'update:show', ...args: any[]): void
}>();

const save = async () => {
  state.loadingSave = true;

  if (state.passwordForm) {
    // saving new password
    // saving new password
    const res = await teachersS.resetPassword(props.teacher.uid, state.newPassword);
    useAlertsStore().addAlert(
      res ? "success" : "error",
      res ? "Teacher Password updated successfully" : "Failed to update teacher password",
      7
    );
  } else {
    // save teacher

    const res = await teachersS.updateTeacher(props.teacher.uid, {
      teacher_title: state.teacher_title,
      teacher_fname: state.teacher_fname,
      teacher_lname: state.teacher_lname,
      teacher_classes: state.teacher_classes,
      room_assignment: state.room_assignment,
      virtual_room: state.virtual_room,
      email: state.email,
      phone: state.phone
    });

    if (res.teacher) {
      useAlertsStore().addAlert("success", res.message, 7);
    }
  }
  state.loadingSave = false;
};

const setDelete = async (val: boolean) => {
  state.loadingDelete = true;

  if (state.passwordForm) {
    // saving new password
  } else {
    // save teacher

    const res = await teachersS.updateTeacher(props.teacher.uid, {
      deleted: val
    });

    const message = val ? "delete" : "restore";
    useAlertsStore().addAlert(
      res.teacher ? "success" : "error",
      res.teacher ? `Teacher ${message}d successfully` : `Failed to ${message} teacher`,
      7
    );
  }
  state.loadingDelete = false;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.modal-container {
  padding: 30px;

  .modal-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .modal-body {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 30px;
  }
}
</style>
